import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
// ONE shared source for payment terms, so this document can never drift from the screens
// that set them (see utils/paymentTerms.js).
import { paymentTermsLabel } from '../utils/paymentTerms';

// ============================================
// SUBBIE PURCHASE ORDER (PDF)
// Same skeleton + branding as the Proforma / invoice PDFs: the company header bar with
// the logo on the left and the company details on the right, so the logo renders exactly
// as it does everywhere else.
// 🔒 Shows the SUBCONTRACTOR'S cost only - never the client price or our margin.
// ============================================

const NAVY = '#0A1929';
const GREEN = '#00A86B';

const styles = StyleSheet.create({
  page: { fontFamily: 'Helvetica', fontSize: 9, color: NAVY, backgroundColor: '#FFFFFF', paddingBottom: 40 },
  header: { backgroundColor: NAVY, paddingVertical: 14, paddingHorizontal: 30 },
  headerContent: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  headerLeft: { flexDirection: 'column' },
  headerLogo: { width: 120, height: 50, objectFit: 'contain' },
  headerDetails: { textAlign: 'right' },
  headerDetailText: { fontSize: 8, color: '#FFFFFF', marginBottom: 1 },
  content: { paddingHorizontal: 30, paddingTop: 22, flex: 1 },
  title: { fontSize: 18, fontWeight: 'bold', color: NAVY, marginBottom: 2 },
  subtitle: { fontSize: 9, color: '#64748B', marginBottom: 14 },
  infoRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8, padding: 6, backgroundColor: '#F5F7FA', borderRadius: 4 },
  infoItem: { flexDirection: 'column', flex: 1 },
  infoLabel: { fontSize: 7, color: '#666666', fontWeight: 'bold', textTransform: 'uppercase' },
  infoValue: { fontSize: 10, color: NAVY, fontWeight: 'bold', marginTop: 2 },
  twoCol: { flexDirection: 'row', gap: 20, marginBottom: 12 },
  col: { flex: 1 },
  sectionTitle: { fontSize: 9, fontWeight: 'bold', color: NAVY, marginBottom: 4, textTransform: 'uppercase' },
  sectionText: { fontSize: 9, color: '#374151', marginBottom: 2 },
  tableHead: { flexDirection: 'row', backgroundColor: NAVY, paddingVertical: 5, paddingHorizontal: 6, borderRadius: 3 },
  th: { color: '#FFFFFF', fontSize: 7, fontWeight: 'bold', textTransform: 'uppercase' },
  tableRow: { flexDirection: 'row', paddingVertical: 5, paddingHorizontal: 6, borderBottomWidth: 1, borderBottomColor: '#EEF2F6' },
  td: { fontSize: 9, color: '#1F2937' },
  cLine: { width: '9%' },
  cDesc: { width: '47%' },
  cQty: { width: '10%', textAlign: 'right' },
  cUnit: { width: '10%', textAlign: 'center' },
  cRate: { width: '12%', textAlign: 'right' },
  cTotal: { width: '12%', textAlign: 'right' },
  totalsWrap: { flexDirection: 'row', justifyContent: 'flex-end', marginTop: 10 },
  totals: { width: '48%' },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 3 },
  totalLabel: { fontSize: 9, color: '#4A5568' },
  totalValue: { fontSize: 9, color: NAVY, fontWeight: 'bold' },
  grandRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6, paddingHorizontal: 6, backgroundColor: GREEN, borderRadius: 3, marginTop: 4 },
  grandLabel: { fontSize: 10, color: '#FFFFFF', fontWeight: 'bold' },
  grandValue: { fontSize: 11, color: '#FFFFFF', fontWeight: 'bold' },
  note: { marginTop: 16, padding: 8, backgroundColor: '#F0FDF9', borderRadius: 4, borderWidth: 1, borderColor: '#D1FAE9' },
  noteText: { fontSize: 8, color: '#065F46', lineHeight: 1.4 },
  footer: { position: 'absolute', bottom: 18, left: 30, right: 30, borderTopWidth: 1, borderTopColor: '#E8ECEF', paddingTop: 6 },
  footerText: { fontSize: 7, color: '#94A3B8', textAlign: 'center' }
});

const money = (n) => `R ${(Number(n) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

// The logo is stored as a site-relative path (e.g. /Images/...png); PDF rendering needs an
// absolute URL. Same helper the proforma / invoice PDFs use.
const resolveLogo = (logo) => {
  if (!logo) return null;
  if (logo.startsWith('http://') || logo.startsWith('https://') || logo.startsWith('data:')) return logo;
  if (typeof window !== 'undefined') return window.location.origin + logo;
  return logo;
};

const PurchaseOrderPDF = ({ purchaseOrder, job, subcontractor, items = [], company }) => {
  const c = company || {};
  const fullAddress = [c.company_address, c.company_address_line2].filter(Boolean).join(', ');
  const logoSrc = resolveLogo(c.company_logo);
  const po = purchaseOrder || {};
  const subbie = subcontractor || {};
  const subbieEmail = subbie.contact_email || subbie.email || null;
  const subbiePhone = subbie.contact_phone || subbie.phone || null;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* HEADER - identical structure to the proforma so the logo lines up the same */}
        <View style={styles.header}>
          <View style={styles.headerContent}>
            <View style={styles.headerLeft}>
              {logoSrc && <Image src={logoSrc} style={styles.headerLogo} />}
            </View>
            <View style={styles.headerDetails}>
              {fullAddress ? <Text style={styles.headerDetailText}>{fullAddress}</Text> : null}
              {c.company_phone ? <Text style={styles.headerDetailText}>Tel: {c.company_phone}</Text> : null}
              {c.company_email ? <Text style={styles.headerDetailText}>Email: {c.company_email}</Text> : null}
              {c.company_website ? <Text style={styles.headerDetailText}>Web: {c.company_website}</Text> : null}
              {c.company_reg_number ? <Text style={styles.headerDetailText}>Reg: {c.company_reg_number}</Text> : null}
              {c.company_vat_number ? <Text style={styles.headerDetailText}>VAT: {c.company_vat_number}</Text> : null}
            </View>
          </View>
        </View>

        <View style={styles.content}>
          <Text style={styles.title}>PURCHASE ORDER</Text>
          <Text style={styles.subtitle}>Issued by {c.company_name || 'One Purpose Logistics'}</Text>

          <View style={styles.infoRow}>
            <View style={styles.infoItem}><Text style={styles.infoLabel}>P.O. Number</Text><Text style={styles.infoValue}>{po.po_number || '—'}</Text></View>
            <View style={styles.infoItem}><Text style={styles.infoLabel}>Date</Text><Text style={styles.infoValue}>{fmtDate(po.po_date)}</Text></View>
            <View style={styles.infoItem}><Text style={styles.infoLabel}>Job Number</Text><Text style={styles.infoValue}>{job?.job_number || '—'}</Text></View>
            {/* The subbie's REAL agreed terms. This used to fall back to a hardcoded "30 Days",
                so a PO could state payment terms nobody had agreed — on a document the subbie
                agreement names as their payment confirmation. Now it reads "Not set" instead. */}
            <View style={styles.infoItem}><Text style={styles.infoLabel}>Payment Terms</Text><Text style={styles.infoValue}>{paymentTermsLabel(subbie.payment_terms)}</Text></View>
          </View>

          <View style={styles.twoCol}>
            <View style={styles.col}>
              <Text style={styles.sectionTitle}>Subcontractor</Text>
              <Text style={styles.sectionText}>{subbie.company_name || '—'}</Text>
              {subbie.contact_person ? <Text style={styles.sectionText}>Attn: {subbie.contact_person}</Text> : null}
              {subbieEmail ? <Text style={styles.sectionText}>{subbieEmail}</Text> : null}
              {subbiePhone ? <Text style={styles.sectionText}>{subbiePhone}</Text> : null}
              {subbie.physical_address ? <Text style={styles.sectionText}>{subbie.physical_address}</Text> : null}
              {subbie.vat_number ? <Text style={styles.sectionText}>VAT: {subbie.vat_number}</Text> : null}
            </View>
            <View style={styles.col}>
              <Text style={styles.sectionTitle}>The Work</Text>
              <Text style={styles.sectionText}>Job: {job?.job_number || '—'}</Text>
              {job?.commodity ? <Text style={styles.sectionText}>Commodity: {job.commodity}</Text> : null}
              {job?.origin ? <Text style={styles.sectionText}>From: {job.origin}</Text> : null}
              {job?.destination ? <Text style={styles.sectionText}>To: {job.destination}</Text> : null}
              {job?.start_date ? <Text style={styles.sectionText}>Start: {fmtDate(job.start_date)}</Text> : null}
            </View>
          </View>

          <View style={styles.tableHead}>
            <Text style={[styles.th, styles.cLine]}>Line</Text>
            <Text style={[styles.th, styles.cDesc]}>Description</Text>
            <Text style={[styles.th, styles.cQty]}>Qty</Text>
            <Text style={[styles.th, styles.cUnit]}>Unit</Text>
            <Text style={[styles.th, styles.cRate]}>Rate</Text>
            <Text style={[styles.th, styles.cTotal]}>Amount</Text>
          </View>

          {items.map((it) => (
            <View key={it.id} style={styles.tableRow} wrap={false}>
              <Text style={[styles.td, styles.cLine]}>L{it.line_number}</Text>
              <Text style={[styles.td, styles.cDesc]}>{it.title}</Text>
              <Text style={[styles.td, styles.cQty]}>{it.quantity}</Text>
              <Text style={[styles.td, styles.cUnit]}>{it.unit}</Text>
              <Text style={[styles.td, styles.cRate]}>{money(it.rate)}</Text>
              <Text style={[styles.td, styles.cTotal]}>{money(it.line_total)}</Text>
            </View>
          ))}
          {items.length === 0 && (
            <Text style={[styles.td, { padding: 8, color: '#94A3B8' }]}>No line items assigned.</Text>
          )}

          <View style={styles.totalsWrap}>
            <View style={styles.totals}>
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Subtotal (excl VAT)</Text>
                <Text style={styles.totalValue}>{money(po.subtotal)}</Text>
              </View>
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>VAT @ 15%</Text>
                <Text style={styles.totalValue}>{money(po.vat_amount)}</Text>
              </View>
              <View style={styles.grandRow}>
                <Text style={styles.grandLabel}>TOTAL</Text>
                <Text style={styles.grandValue}>{money(po.total_amount)}</Text>
              </View>
            </View>
          </View>

          <View style={styles.note}>
            <Text style={styles.noteText}>
              A signed copy of this Purchase Order must accompany your invoice - it serves as the payment confirmation.
              Any extra, additional or re-attempted work requires an ADDITIONAL purchase order before it is carried out.
            </Text>
          </View>
        </View>

        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>
            {c.company_name || 'One Purpose Logistics'}
            {c.company_reg_number ? ` · Reg: ${c.company_reg_number}` : ''}
            {c.company_vat_number ? ` · VAT: ${c.company_vat_number}` : ''}
            {c.company_phone ? ` · ${c.company_phone}` : ''}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default PurchaseOrderPDF;

